import { httpInstance } from '@/clients/http.request';
import {
   urlClient,
   urlFlujoAutomotor,
   urlFiles,
   urlMailBackend,
} from '@/helpers';
import { showWarning } from '@/composables/useToast';
import {
   buildEntradaNoDisponible,
   evaluarAntesDeCotizar,
   obtenerEvaluador,
} from '../evaluadores';

const COMPANIAS_SIN_TOAST_COTIZACION = ['el norte', 'la caja', 'caja de seguros'];

const shouldShowToastCotizacionFallida = (nombre) => {
   const normalized = String(nombre || '')
      .toLowerCase()
      .trim();
   return !COMPANIAS_SIN_TOAST_COTIZACION.some(
      (fragment) =>
         normalized === fragment || normalized.includes(fragment),
   );
};

const calcularMetadata = (cotizaciones) => {
   if (!cotizaciones || cotizaciones.length === 0) {
      return {
         totalCotizaciones: 0,
         totalesPorCobertura: {},
      };
   }

   const totalesPorCobertura = {};

   cotizaciones.forEach((cotizacion) => {
      if (cotizacion.tipoCobertura) {
         totalesPorCobertura[cotizacion.tipoCobertura] =
            (totalesPorCobertura[cotizacion.tipoCobertura] || 0) + 1;
      }
   });

   return {
      totalCotizaciones: cotizaciones.length,
      totalesPorCobertura,
   };
};

export const cotizar = async ({ commit, getters, state }) => {
   const datosCotizacion = getters.datosCotizacion;

   commit('setCotizaciones', []);
   commit('resetLoadingCotizaciones');
   commit('clearCompaniasNoDisponibles');

   const cotizarConTimeout = async (endpoint, timeout = 30000) => {
      let timeoutId;
      try {
         const result = await Promise.race([
            httpInstance.post(
               `${urlFlujoAutomotor}${endpoint}`,
               datosCotizacion,
            ),
            new Promise((_, reject) => {
               timeoutId = setTimeout(
                  () => reject(new Error('Timeout')),
                  timeout,
               );
            }),
         ]);
         if (timeoutId) clearTimeout(timeoutId);
         return result;
      } catch (error) {
         if (timeoutId) clearTimeout(timeoutId);
         throw error;
      }
   };

   // Obtener compañías habilitadas del state
   const companiasHabilitadas = state.companiasHabilitadas || [];

   const companiasACotizar = [];

   for (const compania of companiasHabilitadas) {
      const evaluacion = evaluarAntesDeCotizar(
         compania.id_compania,
         datosCotizacion,
      );
      if (!evaluacion.disponible) {
         commit(
            'addCompaniaNoDisponible',
            buildEntradaNoDisponible(compania, evaluacion.motivo),
         );
         continue;
      }
      companiasACotizar.push(compania);
   }

   // Si no hay compañías habilitadas, retornar sin hacer nada
   if (companiasACotizar.length === 0) {
      commit('setLoadingCotizacion', { loading: false });
      return {
         cotizaciones: [],
         metadata: calcularMetadata([]),
         errores: undefined,
      };
   }

   // Crear promesas dinámicamente basadas en las compañías a cotizar
   const promises = companiasACotizar.map((compania) => {
      const { id_compania, nombre } = compania;
      const evaluador = obtenerEvaluador(id_compania);

      return cotizarConTimeout(`/cotizacion/${id_compania}`)
         .then((response) => {
            const cotizaciones = response.data?.cotizaciones ?? [];
            if (cotizaciones.length > 0) {
               commit('addCotizaciones', cotizaciones);
            } else if (evaluador?.usaCardGris) {
               commit(
                  'addCompaniaNoDisponible',
                  buildEntradaNoDisponible(compania, evaluador.mensajeArrayVacio),
               );
            }
            return { success: true, compania: nombre };
         })
         .catch((error) => {
            if (evaluador?.usaCardGris) {
               commit(
                  'addCompaniaNoDisponible',
                  buildEntradaNoDisponible(compania, evaluador.mensajeErrorApi),
               );
            } else if (shouldShowToastCotizacionFallida(nombre)) {
               showWarning(`No hay cotizacion disponible en ${nombre}`);
            }
            console.warn(`Error en ${nombre}:`, error);
            return { success: false, compania: nombre, error };
         });
   });

   const results = await Promise.allSettled(promises);

   commit('setLoadingCotizacion', { loading: false });

   const todasCotizaciones = state.cotizaciones || [];
   const metadata = calcularMetadata(todasCotizaciones);
   commit('setMetadataCotizaciones', metadata);

   const errores = results
      .filter((r) => r.status === 'rejected')
      .map((r) => r.reason);

   return {
      cotizaciones: todasCotizaciones,
      metadata,
      errores: errores.length > 0 ? errores : undefined,
   };
};

export const getCompaniasHabilitadas = async ({ commit }) => {
   try {
      const { data } = await httpInstance.get(
         `${urlFlujoAutomotor}/companias-habilitadas`,
      );
      commit('setCompaniasHabilitadas', data);
      return data;
   } catch (error) {
      console.error('Error al obtener las compañías habilitadas:', error);
      throw error;
   }
};

export const getMarcas = async ({ commit }) => {
   try {
      const { data } = await httpInstance.get(`${urlFlujoAutomotor}/marcas`);
      commit('setMarcas', data);
   } catch (error) {
      console.error('Error al obtener las marcas:', error);
      throw error;
   }
};

export const getModelos = async ({ commit }, { idMarca, anio, ceroKm }) => {
   try {
      const { data } = await httpInstance.get(`${urlFlujoAutomotor}/modelos`, {
         anio: anio,
         idMarca: idMarca,
         ceroKm: ceroKm,
      });
      commit('setModelos', data);
   } catch (error) {
      console.error('Error al obtener los modelos:', error);
      throw error;
   }
};

export const getTipoIva = async ({ commit }) => {
   try {
      const { data } = await httpInstance.get(`${urlClient}/tipoIva`);
      commit('setTipoIva', data);
   } catch (error) {
      console.error('Error al obtener los tipos de IVA:', error);
      throw error;
   }
};

export const getTipoUso = async ({ commit }) => {
   try {
      const { data } = await httpInstance.get(`${urlClient}/tipoUso`);
      commit('setTipoUso', data);
   } catch (error) {
      console.error('Error al obtener los tipos de uso:', error);
      throw error;
   }
};

export const getActividades = async ({ commit }) => {
   try {
      const { data } = await httpInstance.get(
         `${urlFlujoAutomotor}/actividades/mercantil`,
      );
      const actividades = Array.isArray(data) ? data : [];
      const actividadesFormateadas = actividades
         .map((actividad) => ({
            label:
               actividad.nombre ||
               actividad.name ||
               actividad.label ||
               actividad.descripcion ||
               '',
            value: actividad.id || actividad.value || actividad.codigo || '',
         }))
         .filter((act) => act.label && act.value);
      commit('setActividades', actividadesFormateadas);
   } catch (error) {
      console.error('Error al obtener las actividades:', error);
      throw error;
   }
};

export const getIngresosBrutos = async ({ commit }) => {
   try {
      const { data } = await httpInstance.get(
         `${urlFlujoAutomotor}/ingresosBrutos`,
      );
      const ingresosBrutos = Array.isArray(data) ? data : [];
      const ingresosBrutosFormateados = ingresosBrutos
         .map((item) => ({
            label: item.nombre || item.name || item.label || '',
            value: item.id || item.value || '',
         }))
         .filter((item) => item.label && item.value);
      commit('setIngresosBrutos', ingresosBrutosFormateados);
   } catch (error) {
      console.error('Error al obtener los ingresos brutos:', error);
      throw error;
   }
};

export const getTipoRastreador = async ({ commit }) => {
   try {
      const { data } = await httpInstance.get(
         `${urlFlujoAutomotor}/tipoRastreador`,
      );
      const tipoRastreador = Array.isArray(data) ? data : [];
      const tipoRastreadorFormateado = tipoRastreador
         .map((item) => ({
            label: item.label || item.nombre || item.name || '',
            value: item.value || item.id || '',
         }))
         .filter((item) => item.label && item.value);
      commit('setTipoRastreador', tipoRastreadorFormateado);
   } catch (error) {
      console.error('Error al obtener los tipos de rastreador:', error);
      throw error;
   }
};

export const getOpcionesFinanciamiento = async ({ commit }) => {
   try {
      const response = await httpInstance.get(
         `${urlFlujoAutomotor}/opciones-financiamiento`,
      );

      const data = response?.data || {};

      commit('setOpcionesFinanciamiento', {
         esPrendaLeasing: data.esPrendaLeasing,
         esPrendaLeasingEditable: data.esPrendaLeasingEditable,
      });

      return data;
   } catch (error) {
      console.error(
         'Error al obtener las opciones de financiamiento:',
         error,
      );
      throw error;
   }
};

export const uploadFileAutomotor = async ({ rootState }, data) => {
   const formData = new FormData();

   if (Array.isArray(data.files)) {
      data.files.forEach((file) => {
         formData.append(data.type, file);
      });
   } else {
      formData.append(data.type, data.files);
   }
   formData.append('id_poliza', rootState.poliza.poliza.hash_id);
   formData.append('type', data.type);
   // Explícito siempre: el backend reemplaza por defecto, así que omitir el
   // campo cuando no corresponde reemplazar (subidas incrementales dentro de
   // la misma sesión) haría que el backend igual reemplace.
   formData.append('reemplazar', data.reemplazar ? 'true' : 'false');

   try {
      const response = await httpInstance.post(
         `${urlFiles}/uploadFileAutomotor`,
         formData,
         true,
      );
      return response.data;
   } catch (error) {
      throw error;
   }
};

export const deleteFileAutomotor = async (_, id_documento) => {
   try {
      await httpInstance.delete(
         `${urlFiles}/delete?id_documento=${id_documento}`,
      );
      return { success: true };
   } catch (error) {
      throw error;
   }
};

export const emisionMercantil = async (
   { commit },
   { hash_id, vehiculo, tomador, acreedorPrendario, pago },
) => {
   try {
      const response = await httpInstance.post(
         `${urlFlujoAutomotor}/emision/mercantil`,
         { hash_id, vehiculo, tomador, acreedorPrendario, pago },
      );
      return response.data;
   } catch (error) {
      throw error;
   }
};

export const getUrlInspeccion = async (
   { commit },
   { idCotizacion, dominio, nombre, apellido },
) => {
   try {
      const response = await httpInstance.get(
         `${urlFlujoAutomotor}/url-inspeccion`,
         { idCotizacion, dominio, nombre, apellido },
      );
      return response.data;
   } catch (error) {
      throw error;
   }
};

export const setSeleccionCotizacion = (
   { commit, rootState, state },
   { cotizacionId, seleccionada },
) => {
   if (seleccionada) {
      state.cotizaciones.forEach((cot) => {
         if (cot.id !== cotizacionId) {
            cot.seleccionada = false;
         }
      });
   }
   commit('setSeleccionCotizacion', { cotizacionId, seleccionada });
   if (seleccionada) {
      const cotizacion = state.cotizaciones.find((c) => c.id === cotizacionId);
      if (cotizacion && rootState.poliza?.poliza) {
         rootState.poliza.poliza.id_compania = cotizacion.idCompania;
      }
   }
};

export const confirmarCotizacion = async (
   { rootState, state },
   cotizacionId,
) => {
   try {
      const cotizacion = state.cotizaciones.find((c) => c.id === cotizacionId);
      if (!cotizacion) {
         throw new Error('Cotización no encontrada');
      }

      const hash_id = rootState.poliza?.poliza?.hash_id;
      if (!hash_id) {
         throw new Error('No se encontró el hash_id de la poliza');
      }

      const response = await httpInstance.post(
         `${urlFlujoAutomotor}/confirmar-cotizacion`,
         {
            hash_id,
            idCotizacion: cotizacion.idCotizacion,
            id_compania: cotizacion.idCompania,
            codigoProducto: cotizacion.codigoProducto,
            sumaAsegurada: cotizacion.sumaAsegurada || 0,
            prima: cotizacion.prima || 0,
            premio: cotizacion.premio || 0,
            es_prenda_leasing:
               rootState.automotor?.formulario?.financiamiento
                  ?.esPrendaLeasing ?? true,
            retomado: rootState.poliza?.retomado === true,
            ...(cotizacion.metadataElNorte
               ? {
                    metadataElNorte: cotizacion.metadataElNorte,
                    alternativaNumero:
                       cotizacion.metadataElNorte.alternativaNumero,
                 }
               : {}),
         },
      );

      return response.data;
   } catch (error) {
      console.error('Error al confirmar cotización:', error);
      throw error;
   }
};

export const emitir = async ({ rootState }) => {
   try {
      const hash_id = rootState.poliza?.poliza?.hash_id;
      if (!hash_id) {
         throw new Error('No se encontró el hash_id de la poliza');
      }

      if (rootState.poliza?.poliza?.id_compania === 4) {
         // San Cristobal
         const response = await httpInstance.post(
            `${urlFlujoAutomotor}/emision/sancristobal`,
            {
               hash_id,
               paymentInfo: {
                  formaPago: rootState.automotor.formulario.pago.canalFormaPago,
                  cbuOrTarjeta:
                     rootState.automotor.formulario.pago.cbuOrTarjeta,
                  entidadFinanciera:
                     rootState.automotor.formulario.pago.entidadFinanciera,
               },
               vehicleInfo: rootState.automotor.formulario.vehiculo,
            },
         );
         return response.data;
      } else if (rootState.poliza?.poliza?.id_compania === 54) {
         // Mercantil Andina

         const response = await httpInstance.post(
            `${urlFlujoAutomotor}/emision/mercantil`,
            {
               hash_id: rootState.poliza?.poliza?.hash_id,
               vehiculo: rootState.automotor.formulario.vehiculo,
               tomador: {
                  ...rootState.cliente.cliente,
                  numeroDocumento: rootState.cliente.cliente.cuit,
               },
               acreedorPrendario:
                  rootState.automotor.formulario.acreedorPrendario,
               pago: rootState.automotor.formulario.pago,
            },
         );
         return response.data;
      } else if (rootState.poliza?.poliza?.id_compania === 10) {
         // Sancor
         const response = await httpInstance.post(
            `${urlFlujoAutomotor}/emision/sancor`,
            {
               hash_id,
               pago: rootState.automotor.formulario.pago,
               vehiculo: rootState.automotor.formulario.vehiculo,
            },
         );
         return response.data;
      } else if (rootState.poliza?.poliza?.id_compania === 78) {
         // El Norte
         const response = await httpInstance.post(
            `${urlFlujoAutomotor}/emision/elnorte`,
            {
               hash_id,
               pago: rootState.automotor.formulario.pago,
               vehiculo: rootState.automotor.formulario.vehiculo,
            },
         );
         return response.data;
      } else {
         showError('La compañía seleccionada no es válida');
         return;
      }
   } catch (error) {
      throw error;
   }
};

export const descargarFrentePolizaPdf = async ({ rootState }, hashId) => {
   const hash_id = hashId ?? rootState.poliza?.poliza?.hash_id;
   if (!hash_id) {
      throw new Error('No se encontró el hash_id de la póliza');
   }

   try {
      const response = await httpInstance.get(
         `${urlFlujoAutomotor}/frente-poliza-pdf`,
         { hash_id },
         {
            responseType: 'blob',
         },
      );

      const certificado = rootState.poliza?.poliza?.id_certificado || hash_id;
      const nombreArchivo = String(certificado).replace(/\|/g, '-');
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `frente-poliza-${nombreArchivo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
   } catch (error) {
      throw error;
   }
};

export const sendAutomotorAltaMail = async ({ rootState }, payload = {}) => {
   const hash_id = payload.hash_id ?? rootState.poliza?.poliza?.hash_id;
   if (!hash_id) return;
   try {
      const pdfBlob = payload.pdfBlob;
      if (pdfBlob instanceof Blob) {
         const formData = new FormData();
         formData.append('hash_id', hash_id);
         formData.append(
            'pdf',
            new File([pdfBlob], 'CotizacionAutomotor.pdf', {
               type: 'application/pdf',
            }),
         );
         await httpInstance.post(
            `${urlMailBackend}/sendAutomotorAltaMail`,
            formData,
            {
               headers: {
                  'Content-Type': 'multipart/form-data',
               },
            },
         );
      } else {
         await httpInstance.post(`${urlMailBackend}/sendAutomotorAltaMail`, {
            hash_id,
         });
      }
   } catch (err) {
      console.error('Error al enviar correo de confirmación:', err);
   }
};

export const getAccesorios = async ({ commit }) => {
   try {
      const { data } = await httpInstance.get(
         `${urlFlujoAutomotor}/accesoriosOptions`,
      );
      commit('automotor/setAccesorios', data, { root: true });
      return data;
   } catch (error) {
      throw error;
   }
};

export const getCanalPagoOptions = async ({ commit, rootState }) => {
   try {
      const id_compania = rootState.poliza?.poliza?.id_compania;
      const id_seccion = rootState.poliza?.poliza?.id_seccion;
      if (!id_compania || !id_seccion) {
         throw new Error('No se encontró la compañía o la sección');
      }
      const { data } = await httpInstance.post(
         `${urlFlujoAutomotor}/canalPagoOptions`,
         {
            id_compania,
            id_seccion,
         },
      );
      commit('setCanalPagoOptions', data.medios_pago);
      commit('setEntidadesFinancieras', data.entidades);
   } catch (error) {
      throw error;
   }
};

export const getFotosRequeridasElNorte = async ({ commit, rootState }) => {
   try {
      const hash_id = rootState.poliza?.poliza?.hash_id;
      if (!hash_id) {
         commit('setFotosRequeridasElNorte', []);
         return [];
      }
      const { data } = await httpInstance.post(
         `${urlFlujoAutomotor}/elnorte/fotos-requeridas`,
         { hash_id },
      );
      const fotos = Array.isArray(data?.fotos) ? data.fotos : [];
      commit('setFotosRequeridasElNorte', fotos);
      return fotos;
   } catch (error) {
      console.error('Error al obtener fotos requeridas El Norte:', error);
      commit('setFotosRequeridasElNorte', []);
      return [];
   }
};
